import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, Alert, addToast } from '@octano/global-ui';
import { Card } from 'reactstrap';
import { TranslationsKeys } from '../../../../locales/translations';
import { useReportsColumns } from './hooks/useReportsColumns';
import { ReportAsyncGroup, ReportAsyncTypeRow } from './types';
import GenerateReportModal, {
  GenerateReportModalMethods,
} from './parts/GenerateReportModal';
import ReportHistoryModal, {
  ReportHistoryModalMethods,
} from './parts/ReportHistoryModal';
import { getAsyncReports } from './requests';
import { useRefetchHook } from './hooks/useRefetchHook';

type ReportAsyncProps = {
  group?: ReportAsyncGroup;
};

const descriptions: Record<ReportAsyncGroup, string> = {
  [ReportAsyncGroup.Coursables]: 'descriptionCoursables',
  [ReportAsyncGroup.SIES]: 'descriptionSIES',
  [ReportAsyncGroup.Default]: 'description',
};

const ReportAsync = ({
  group = ReportAsyncGroup.Default,
}: ReportAsyncProps) => {
  const { t } = useTranslation(TranslationsKeys.MANAGEMENT_TOOLS_REPORTS_ASYNC);
  const generatorRef = useRef<GenerateReportModalMethods>(null);
  const historyRef = useRef<ReportHistoryModalMethods>(null);

  const [mounted, setMounted] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [refetching, setRefetching] = useState<boolean>(false);
  const [items, setItems] = useState<ReportAsyncTypeRow[]>([]);

  const isGenerating = useMemo(
    () => items?.some((item) => !!item?.generating),
    [items],
  );

  const handlePressGenerate = useCallback((row: ReportAsyncTypeRow) => {
    generatorRef?.current?.open(row);
  }, []);

  const handlePressHistory = useCallback((row: ReportAsyncTypeRow) => {
    historyRef?.current?.open(row);
  }, []);

  const columns = useReportsColumns({
    onPressGenerate: handlePressGenerate,
    onPressHistory: handlePressHistory,
  });

  const fetchData = useCallback(
    async (isRefetch?: boolean) => {
      if (loading) {
        return;
      }
      setLoading(true);
      if (isRefetch) {
        setRefetching(true);
      }
      try {
        const { data = [], error } = await getAsyncReports(group);
        if (!error && data?.length) {
          setItems(data);
        } else {
          setItems([]);
        }
        if (isRefetch && !data?.some((e) => !!e.generating)) {
          addToast({
            icon: 'success',
            color: 'success',
            text: t(`alerts.generated`),
          });
        }
      } catch (error) {
        setItems([]);
      }
      setRefetching(false);
      setLoading(false);
    },
    [loading, group, t],
  );

  useRefetchHook(() => fetchData(true), !!isGenerating, loading);

  useEffect(() => {
    if (!mounted) {
      fetchData();
      setMounted(true);
    }
  }, [mounted, fetchData]);

  return (
    <>
      <Card className="p-4">
        <p className="mb-4 mt-2">{t(descriptions[group] || 'default')}</p>
        {!!isGenerating && (
          <Alert
            color="info"
            icon="information"
            noFill
            size="sm"
            text={t(`alerts.isGenerating`)}
            isOpen={true}
          />
        )}
        <Table
          columns={columns}
          data={items}
          isLoadingResults={loading && !refetching}
        />
      </Card>
      <GenerateReportModal ref={generatorRef} onGenerate={fetchData} />
      <ReportHistoryModal ref={historyRef} />
    </>
  );
};

export default ReportAsync;
